import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { Box, Grid } from '@mui/material';
import { FormEventHandler, useCallback } from 'react';
import { FieldError, UseFormReturn, Control } from 'react-hook-form';
import { useQualificationOptionsQuery } from '../../../store/features/qualification/qualificationSlice';
import { GoogleMapAddress } from '../../../types/util';
import { ControlledAutocompleteInput } from '../../controlled-autocomplete-input/ControlledAutocompleteInput';
import { DateInput } from '../../date-input/DateInput';
import { GoogleMapLocationField } from '../../google-map-location-field/GoogleMapLocationField';
import { TextInput } from '../../text-input/TextInput';
import { QualificationInputs, QualificationWithAddressInputs } from '../types';
import { ShareQualificationHeader } from './QualificationFormHeader';

interface QualificationInputUpsertFormProps<IncludeAddressField extends boolean> {
  SubmitButtonProps?: LoadingButtonProps;
  includeAddressField?: IncludeAddressField;
  form: UseFormReturn<IncludeAddressField extends true ? QualificationWithAddressInputs : QualificationInputs>;
  onSubmit: FormEventHandler;
  SubmitButtonLabel: string;
  isSubmitting?: boolean;
}
export const QualificationInputUpsertForm = <T extends boolean>({
  SubmitButtonProps,
  includeAddressField,
  form,
  onSubmit,
  SubmitButtonLabel,
  isSubmitting,
}: QualificationInputUpsertFormProps<T>) => {
  const { isError, isLoading, data: options } = useQualificationOptionsQuery();

  const _form = includeAddressField
    ? (form as unknown as UseFormReturn<QualificationWithAddressInputs>)
    : (form as unknown as UseFormReturn<QualificationWithAddressInputs>);

  const {
    formState: { errors },
    register,
    control,
    setValue,
    getValues,
  } = _form;

  const updateAddress = useCallback(
    (address: GoogleMapAddress): void => {
      if (includeAddressField) {
        const location = getValues('address');
        if (address.location !== location) {
          setValue('address', address.location, { shouldValidate: location?.trim().length === 0 });
        }
        setValue('address_lat', address.lat);
        setValue('address_lng', address.lng);
      }
    },
    [setValue, getValues, includeAddressField]
  );

  const getDefaultAddress = (): GoogleMapAddress | undefined => {
    if (includeAddressField) {
      return {
        location: getValues('address'),
        lat: getValues('address_lat'),
        lng: getValues('address_lng'),
      };
    }
  };

  const getAddressError = (): string | undefined => {
    if ('address' in errors) {
      return (errors.address as FieldError)?.message;
    }
    if ('address_lat' in errors) {
      return (errors.address_lat as FieldError)?.message;
    }
    if ('address_lng' in errors) {
      return (errors.address_lng as FieldError)?.message;
    }
  };

  return (
    <Box>
      <ShareQualificationHeader />
      <form onSubmit={onSubmit}>
        {includeAddressField && (
          <GoogleMapLocationField
            required
            defaultAddress={getDefaultAddress()}
            FieldProps={register('address')}
            placeholder="Your Current Address"
            label="Your Current Address"
            id="upsert-qualification-address-field"
            LatitudeInputProps={register('address_lat')}
            LongitudeInputProps={register('address_lng')}
            onChange={updateAddress}
            error={getAddressError()}
          />
        )}
        <ControlledAutocompleteInput
          name="educationLevel"
          error={errors.educationLevel?.message}
          control={control}
          id="upsert-qualification-education-field"
          label="Education Level"
          required
          placeholder="Select Education Level"
          isError={isError}
          isLoading={isLoading}
          AutocompleteProps={{ options: options?.education_levels }}
        />
        <ControlledAutocompleteInput
          name="institute"
          error={errors.institute?.message}
          control={control}
          id="upsert-qualification-board-field"
          label="Education Board / University"
          required
          placeholder="Select Education Board / University"
          isError={isError}
          isLoading={isLoading}
          AutocompleteProps={{ options: options?.institutes }}
        />
        <DateInput
          error={errors.graduatedYear?.message}
          id="upsert-qualification-year-field"
          label="Graduation Year"
          required
          placeholder="Select Graduation Year"
          value={getValues('graduatedYear')}
          onChange={(value) => setValue('graduatedYear', value)}
          DatePickerProps={{ disableFuture: true, views: ['year'] }}
        />
        <Grid container columnSpacing={1.5}>
          <Grid item xs={12} sm={4}>
            <TextInput
              label="Percentage"
              id="upsert-qualification-percentage-field"
              FieldProps={register('percentage')}
              placeholder="Enter Percentage"
              error={errors.percentage?.message}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label="GPA"
              id="upsert-qualification-gpa-field"
              FieldProps={register('GPA')}
              placeholder="Enter GPA"
              error={errors.GPA?.message}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ControlledAutocompleteInput
              name="rank"
              error={errors.rank?.message}
              control={control}
              id="upsert-qualification-grade-field"
              label="Grade Levels"
              placeholder="Select Grade"
              isError={isError}
              isLoading={isLoading}
              AutocompleteProps={{ options: options?.ranks }}
            />
          </Grid>
        </Grid>
        <ControlledAutocompleteInput
          name="faculty"
          error={errors.faculty?.message}
          control={control}
          id="upsert-qualification-faculty-field"
          label="Faculty(if any)"
          placeholder="Select Faculty"
          isError={isError}
          isLoading={isLoading}
          AutocompleteProps={{ options: options?.faculties }}
        />
        <ControlledAutocompleteInput<QualificationInputs, true>
          name="course"
          error={(errors.course as unknown as FieldError)?.message}
          control={control as unknown as Control<QualificationInputs>}
          id="upsert-qualification-courses-field"
          label="Courses(if any)"
          placeholder="Select Courses"
          isError={isError}
          isLoading={isLoading}
          AutocompleteProps={{ options: options?.courses || [], multiple: true }}
        />
        <LoadingButton
          loading={isSubmitting}
          type="submit"
          variant="contained"
          sx={{ width: 1 }}
          {...SubmitButtonProps}
        >
          {SubmitButtonLabel}
        </LoadingButton>
      </form>
    </Box>
  );
};
